/*
  Water on the key art.

  The simulation is the classic damped-wave one from sirxemic/jquery.ripples
  (MIT): a height field in a float texture, where each step pulls the height
  toward the average of its four neighbours, keeps the velocity in a second
  channel, and bleeds 0.5% of it per frame. The render pass takes the gradient
  of that field as a surface normal, refracts the photograph through it, and
  adds a specular term so the crests catch light.

  What is different here: no jQuery, no background-image plumbing, one <img>
  drawn as a texture with an object-fit: cover mapping computed on the CPU, and
  drops driven by the pointer's own speed so a fast swipe pushes more water
  than a slow drift.

  It bails to the untouched photograph — never a blank box — when WebGL is
  missing, when float textures are missing, on reduced motion, or on save-data.
*/
(function () {
  'use strict';

  var VERT = 'attribute vec2 vertex;varying vec2 vUv;void main(){vUv=vertex*0.5+0.5;gl_Position=vec4(vertex,0.0,1.0);}';

  var DROP = [
    'precision highp float;',
    'const float PI = 3.141592653589793;',
    'uniform sampler2D uField; uniform vec2 uCenter; uniform float uRadius, uStrength;',
    'varying vec2 vUv;',
    'void main(){',
    '  vec4 info = texture2D(uField, vUv);',
    // raised cosine, so the drop has no edge to it
    '  float drop = max(0.0, 1.0 - length(uCenter * 0.5 + 0.5 - vUv) / uRadius);',
    '  drop = 0.5 - cos(drop * PI) * 0.5;',
    '  info.r += drop * uStrength;',
    '  gl_FragColor = info;',
    '}'
  ].join('\n');

  var STEP = [
    'precision highp float;',
    'uniform sampler2D uField; uniform vec2 uDelta;',
    'varying vec2 vUv;',
    'void main(){',
    '  vec4 info = texture2D(uField, vUv);',
    '  vec2 dx = vec2(uDelta.x, 0.0), dy = vec2(0.0, uDelta.y);',
    '  float average = (texture2D(uField, vUv - dx).r + texture2D(uField, vUv - dy).r',
    '                 + texture2D(uField, vUv + dx).r + texture2D(uField, vUv + dy).r) * 0.25;',
    // r is height, g is velocity; the 0.995 is the damping that lets it settle
    '  info.g += (average - info.r) * 2.0;',
    '  info.g *= 0.995;',
    '  info.r += info.g;',
    '  gl_FragColor = info;',
    '}'
  ].join('\n');

  var DRAW = [
    'precision highp float;',
    'uniform sampler2D uImage, uField;',
    'uniform vec2 uDelta, uUvScale, uUvOffset;',
    'uniform float uPerturbance, uSpecular;',
    'varying vec2 vUv;',
    'void main(){',
    '  float h  = texture2D(uField, vUv).r;',
    '  float hx = texture2D(uField, vec2(vUv.x + uDelta.x, vUv.y)).r;',
    '  float hy = texture2D(uField, vec2(vUv.x, vUv.y + uDelta.y)).r;',
    '  vec3 dx = vec3(uDelta.x, hx - h, 0.0);',
    '  vec3 dy = vec3(0.0, hy - h, uDelta.y);',
    '  vec2 offset = -normalize(cross(dy, dx)).xz;',
    '  float spec = pow(max(0.0, dot(offset, normalize(vec2(-0.6, 1.0)))), 4.0);',
    '  vec2 uv = uUvOffset + vUv * uUvScale + offset * uPerturbance;',
    '  gl_FragColor = texture2D(uImage, clamp(uv, 0.0, 1.0)) + spec * uSpecular;',
    '}'
  ].join('\n');

  function compile(gl, type, src) {
    var s = gl.createShader(type);
    gl.shaderSource(s, src); gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) return null;
    return s;
  }
  function program(gl, vs, fs) {
    var v = compile(gl, gl.VERTEX_SHADER, vs), f = compile(gl, gl.FRAGMENT_SHADER, fs);
    if (!v || !f) return null;
    var p = gl.createProgram();
    gl.attachShader(p, v); gl.attachShader(p, f); gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) return null;
    return p;
  }

  window.SportRipple = function (opts) {
    var host = opts.host, img = opts.image, canvas = opts.canvas;
    if (!host || !img || !canvas) return null;

    var gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: false, antialias: false, depth: false })
          || canvas.getContext('experimental-webgl');
    if (!gl) return null;

    // the height field has to hold signed values with room under them
    var type = null;
    if (gl.getExtension('OES_texture_float') && gl.getExtension('OES_texture_float_linear')) type = gl.FLOAT;
    else {
      var half = gl.getExtension('OES_texture_half_float');
      if (half && gl.getExtension('OES_texture_half_float_linear')) type = half.HALF_FLOAT_OES;
    }
    if (type === null) return null;

    var pDrop = program(gl, VERT, DROP), pStep = program(gl, VERT, STEP), pDraw = program(gl, VERT, DRAW);
    if (!pDrop || !pStep || !pDraw) return null;

    // 256 is the plugin's default and it was built for small elements; across a
    // full-bleed hero each texel covers ~9 device pixels and the wavefront goes
    // blocky. 512 is still two trivial passes.
    var SIM = 512, delta = [1 / SIM, 1 / SIM];

    var quad = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, quad);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);

    function field() {
      var t = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, t);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, SIM, SIM, 0, gl.RGBA, type, null);
      var fb = gl.createFramebuffer();
      gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, t, 0);
      var ok = gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE;
      gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      return ok ? { tex: t, fb: fb } : null;
    }
    var a = field(), b = field();
    if (!a || !b) return null;

    var photo = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, photo);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    try { gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img); }
    catch (e) { return null; }   // cross-origin without CORS

    function bindQuad(p) {
      var loc = gl.getAttribLocation(p, 'vertex');
      gl.bindBuffer(gl.ARRAY_BUFFER, quad);
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    }
    function u(p, n) { return gl.getUniformLocation(p, n); }

    var uvScale = [1, 1], uvOffset = [0, 0], posY = 0.5;
    function fit() {
      var r = host.getBoundingClientRect();
      var dpr = Math.min(window.devicePixelRatio || 1, 1.75);
      canvas.width = Math.max(1, Math.round(r.width * dpr));
      canvas.height = Math.max(1, Math.round(r.height * dpr));
      // object-fit: cover, done on the CPU so the shader stays trivial
      var iw = img.naturalWidth || 1, ih = img.naturalHeight || 1;
      var scale = Math.max(r.width / iw, r.height / ih);
      var dw = iw * scale, dh = ih * scale;
      uvScale = [r.width / dw, r.height / dh];
      // posY is object-position from the TOP; the texture runs bottom-up
      uvOffset = [(1 - uvScale[0]) * 0.5, (1 - uvScale[1]) * (1 - posY)];
    }

    var drops = [];
    function drop(nx, ny, radius, strength) { drops.push([nx, ny, radius, strength]); }

    function pour() {
      if (!drops.length) return;
      gl.viewport(0, 0, SIM, SIM);
      gl.useProgram(pDrop);
      bindQuad(pDrop);
      for (var i = 0; i < drops.length; i++) {
        var d = drops[i];
        gl.bindFramebuffer(gl.FRAMEBUFFER, b.fb);
        gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, a.tex);
        gl.uniform1i(u(pDrop, 'uField'), 0);
        gl.uniform2f(u(pDrop, 'uCenter'), d[0], d[1]);
        gl.uniform1f(u(pDrop, 'uRadius'), d[2]);
        gl.uniform1f(u(pDrop, 'uStrength'), d[3]);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        var t = a; a = b; b = t;
      }
      drops.length = 0;
    }

    function step() {
      gl.viewport(0, 0, SIM, SIM);
      gl.useProgram(pStep); bindQuad(pStep);
      gl.bindFramebuffer(gl.FRAMEBUFFER, b.fb);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, a.tex);
      gl.uniform1i(u(pStep, 'uField'), 0);
      gl.uniform2f(u(pStep, 'uDelta'), delta[0], delta[1]);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      var t = a; a = b; b = t;
    }

    function draw() {
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.useProgram(pDraw); bindQuad(pDraw);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, photo);
      gl.uniform1i(u(pDraw, 'uImage'), 0);
      gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, a.tex);
      gl.uniform1i(u(pDraw, 'uField'), 1);
      gl.uniform2f(u(pDraw, 'uDelta'), delta[0], delta[1]);
      gl.uniform2f(u(pDraw, 'uUvScale'), uvScale[0], uvScale[1]);
      gl.uniform2f(u(pDraw, 'uUvOffset'), uvOffset[0], uvOffset[1]);
      gl.uniform1f(u(pDraw, 'uPerturbance'), opts.perturbance || 0.028);
      gl.uniform1f(u(pDraw, 'uSpecular'), opts.specular == null ? 0.7 : opts.specular);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }

    function clear() {
      [a, b].forEach(function (f) {
        gl.bindFramebuffer(gl.FRAMEBUFFER, f.fb);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
      });
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      drops.length = 0;
    }

    return {
      setPositionY: function (v) { posY = v; },
      /* Flatten the water and draw the photograph clean. The damping is 0.995
         a frame, so a surface still carries a sixth of its energy after six
         seconds — stopping the loop there leaves a visible distortion frozen
         on his face, which is what this is for. */
      settle: function () { clear(); draw(); },
      fit: fit,
      drop: drop,
      frame: function () { pour(); step(); draw(); },
      dispose: function () {
        var lose = gl.getExtension('WEBGL_lose_context');
        if (lose) lose.loseContext();
      }
    };
  };
}());
